import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Select, Spin, Badge, Tag, Tooltip, Popover, Radio, Space } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { ChevronDown, Check, Search, Info } from 'lucide-react';
import { HtmlContent } from '@client/utils/html_content';
import DownIcon from '../../../../img/ant-down-icon.svg';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@client/hooks/useIsMobile';
import { useDebouncedValue } from '@client/hooks/useDebouncedValue';
import { useElasticsearchOptions, useSelectedOptionLabels } from '@client/hooks/useElasticsearchOptions';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';
import CollapsibleTags from './CollapsibleTags';
import TutorialButton from '@client/components/ui/TutorialButton';

/**
 * UniversalElasticsearchSelect — UI-only component.
 *
 * Owns: input text, language selection, mobile-sheet state, label cache.
 * Reads (never writes) options/loading/pagination from useElasticsearchOptions.
 *
 * What this component intentionally does NOT do:
 *   - manage `options`, `currentPage`, `hasMore`, `loading` state
 *   - debounce inside callbacks (we debounce the value, not the function)
 *   - track in-flight requests or sequence numbers
 *   - reset state in effects when filters/facets/language change
 *
 * All of that is RQ's job. See useElasticsearchOptions.js for the rationale.
 */

/**
 * @typedef {Object} ElasticsearchOption
 * @property {string} label - Display label
 * @property {string} value - Option value
 * @property {number} [count] - Result count
 * @property {boolean} [highlighted] - Highlight status
 */

/**
 * @typedef {Object} ElasticsearchSelectProps
 * @property {string} document - Document type for Elasticsearch
 * @property {string} field - Field to search in
 * @property {string} [label] - Label translation key
 * @property {number} [debounceTimeout] - Search debounce in ms
 * @property {number} [pageSize] - Results per page
 * @property {string} [sortBy] - Sort field
 * @property {string} [sortOrder] - Sort direction
 * @property {boolean} [showCount] - Display result counts
 * @property {boolean} [infinite] - Enable infinite scroll
 * @property {any} value - Selected value(s)
 * @property {Function} onChange - Change handler
 * @property {boolean} [showSelectedTags] - Show selected items as tags
 * @property {string} [value_field] - Value field mapping
 * @property {string} [label_field] - Label field mapping
 * @property {Object} [languageFields] - Language field mappings
 * @property {Object} [languageLabels] - Language display labels
 * @property {string} [defaultLanguage] - Default language code
 * @property {boolean} [showLanguageSelector] - Show language switcher
 * @property {Function} [labelTransform] - Function to transform option labels (label, value) => transformedLabel
 */

const CONFIG = {
  DEBOUNCE_TIMEOUT: 150,
  PAGE_SIZE: 20,
  SORT_BY: 'count',
  SORT_ORDER: 'desc',
  STORAGE_PREFIX: 'lang_',
};

const TRANSLATIONS = {
  LOADING: 'Chargement...',
  NO_RESULTS: 'Aucun résultat trouvé',
  SEARCH: 'Rechercher...',
};

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Markup- and accent-insensitive normalization for client-side query
// matching. Mirrors the backend (strip_accents + normalized keyword): what
// ES matched must also match here, otherwise typing "achillee" would have
// the backend return the option and the client drop or mis-rank it.
const normalizeForMatch = (s) =>
  String(s ?? '')
    .replace(/<[^>]*>/g, '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const OptionRenderer = ({ option, showCount }) => {
  // AntD's optionRender wraps our option: `option.data` is the original option
  // object, so our inner data lives at `option.data.data`. Handle both paths.
  const optData = option.data?.data || option.data || {};
  // Plain-text version of the (possibly HTML) label for the hover tooltip,
  // so long truncated names show their full text in a styled AntD Tooltip
  // instead of the browser's native `title` popup (which we suppress via the
  // option's `title: ''`).
  const fullText = stripHtml(String(option.label || ''));
  return (
    <div className="elasticsearch-option">
      <div className="flex-1 min-w-0">
        <Tooltip title={fullText} mouseEnterDelay={0.4} placement="topLeft">
          <span
            className={`option-label ${optData.highlighted ? 'highlighted' : ''}`}
          >
            <HtmlContent html={option.label} />
          </span>
        </Tooltip>
        {optData.matchInfo && (
          // R74: render through HtmlContent so inline formatting in synonym
          // values (e.g. italicised latin names) is kept, not shown as tags.
          <div className="text-xs text-gray-400">
            {optData.matchInfo.label} : <HtmlContent html={optData.matchInfo.value} />
          </div>
        )}
      </div>
      {showCount && optData.count && (
        <Badge count={optData.count} className="option-count" />
      )}
    </div>
  );
};

// Globe icon + the word « Langue » sits to the LEFT of the selector,
// matching the pattern used by CultureTreeSelect (SP-408 rule 14/17).
const LanguageSelector = ({
  selectedLanguage,
  languageLabels,
  onChange,
  langLabel,
}) => (
  <Select
    size="small"
    value={selectedLanguage}
    onChange={onChange}
    options={Object.entries(languageLabels).map(([code, label]) => ({
      value: code,
      label: label,
    }))}
    className="language-selector"
    prefix={
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <GlobalOutlined />
        <span>{langLabel}</span>
      </span>
    }
  />
);

// R73/R83/R84/R85 (27 mai 26): "Type d'affichage" selector for the ennemis
// filter. A compact icon + label TRIGGER (not a wide value dropdown — that
// overflowed the narrow column) that opens a popup with the two display
// modes + their descriptions, per the spec. Choosing a mode swaps the label
// field used to render the enemy options + selected tags.
const DisplayModeSelector = ({
  selectedMode,
  modeLabels,
  modeDescriptions,
  onChange,
  toggleLabel,
}) => {
  const content = (
    <div style={{ maxWidth: 280 }}>
      <div className="font-semibold" style={{ marginBottom: 8 }}>
        Sélectionnez le type d&apos;affichage des maladies
      </div>
      <Radio.Group
        value={selectedMode}
        onChange={(e) => onChange(e.target.value)}
      >
        <Space direction="vertical" size={6}>
          {Object.entries(modeLabels).map(([mode, label]) => (
            <Radio key={mode} value={mode}>
              <span style={{ fontWeight: 500 }}>{label}</span>
              {modeDescriptions?.[mode] ? (
                <span style={{ color: '#8c8c8c' }}>
                  {' '}({modeDescriptions[mode]})
                </span>
              ) : null}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </div>
  );
  return (
    <Popover content={content} trigger="click" placement="bottomRight">
      <button type="button" className="display-mode-trigger">
        <Info size={14} />
        <span>{toggleLabel}</span>
        <ChevronDown size={12} />
      </button>
    </Popover>
  );
};

/**
 * Detect a secondary-field match for an option so the dropdown can explain
 * why the option appeared (e.g. "Synonyme : foo" when query matched a synonym
 * but not the primary name). Pure function over (option, query, searchFields).
 */
function detectMatchInfo(option, query, searchFields) {
  if (
    !query
    || !Array.isArray(searchFields)
    || !option.source
    || !searchFields.some((sf) => typeof sf === 'object')
  ) {
    return null;
  }
  const q = normalizeForMatch(query);

  // R77: a matching synonym is shown even when the primary name ALSO
  // contains the query. "mille" hits both "Achillée millefeuille" (name)
  // and its synonym "Achillée mille-feuille" — suppressing the grey line
  // on primary hits made the synonym appear for "mille-" but not "mille".
  for (const sf of searchFields) {
    if (typeof sf !== 'object' || sf.primary || !sf.sourceField) continue;
    const vals = option.source[sf.sourceField];
    const arr = Array.isArray(vals)
      ? vals
      : vals != null
        ? [vals]
        : [];
    const toStr = (v) =>
      typeof v === 'object' && v !== null
        ? v.name || v.label || v.id || JSON.stringify(v)
        : String(v);
    // R46/R47: only show the synonyms/CAS values that ACTUALLY contain the
    // query. Backend wildcards may match a different field on the same doc
    // (e.g. the substance name) and ES top_hits still returns the full
    // synonyms array; rendering "Synonyme: foo, bar" when neither contains
    // the user's letters is misleading.
    const matching = arr.filter((v) => normalizeForMatch(toStr(v)).includes(q));
    if (matching.length > 0) {
      return {
        label: sf.label || sf.sourceField,
        value: matching.map(toStr).join(', '),
      };
    }
  }
  return null;
}

// R24/R25: rank "starts with" above "contains" within the visible options,
// and drop entries whose primary label AND every searched source field miss
// the query entirely (false positives from cross-field wildcard matches).
function rankAndFilterByQuery(options, query, searchFields) {
  if (!query) return options;
  const q = normalizeForMatch(query);
  const sourcePaths = Array.isArray(searchFields)
    ? searchFields
        .filter((sf) => typeof sf === 'object' && sf.sourceField)
        .map((sf) => sf.sourceField)
    : [];

  const scored = options
    .map((opt) => {
      const lab = normalizeForMatch(opt.label);
      let score;
      if (lab.startsWith(q)) score = 0;
      else if (lab.includes(q)) score = 1;
      else {
        let secondaryHit = false;
        const src = opt.data?.source || {};
        for (const path of sourcePaths) {
          const vals = src[path];
          const arr = Array.isArray(vals) ? vals : (vals != null ? [vals] : []);
          if (arr.some((v) => normalizeForMatch(v).includes(q))) {
            secondaryHit = true;
            break;
          }
        }
        score = secondaryHit ? 2 : 3;
      }
      return { opt, score };
    })
    .filter((e) => e.score < 3);

  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    return String(a.opt.label || '').localeCompare(
      String(b.opt.label || ''),
      undefined,
      { numeric: true, sensitivity: 'base' },
    );
  });
  return scored.map((e) => e.opt);
}

/**
 * Elasticsearch-powered select component with infinite scroll.
 * @component
 */
const ElasticsearchSelect = React.forwardRef(({
  document,
  label = 'common|Select',
  field,
  apiEndpoint = null,
  apiFilters = null,
  debounceTimeout = CONFIG.DEBOUNCE_TIMEOUT,
  pageSize = CONFIG.PAGE_SIZE,
  sortBy = CONFIG.SORT_BY,
  sortOrder = CONFIG.SORT_ORDER,
  showCount = true,
  infinite = true,
  value,
  onChange,
  showSelectedTags = true,
  value_field = null,
  label_field = null,
  languageFields = null,
  languageLabels = null,
  defaultLanguage = 'fr',
  showLanguageSelector = false,
  labelTransform = null,
  filters = null,
  mode = 'multiple',
  placeholder,
  initialLabels = null, // Labels for cold start restoration [{value, label}, ...]
  maxVisibleTags = 1,
  // Cross-field faceting: { [es_field_path]: string[] } of currently selected
  // values from sibling filters. The backend skips the self-field automatically
  // so this dropdown still surfaces every possible value for its own field.
  activeFacets = null,
  // Optional list of ES field paths to union-search against the user's `q`
  // (e.g. a substance dropdown searching name OR cas_numbers OR synonyms).
  searchFields = null,
  // R74/R77 fix: language-aware PRIMARY search fields. When the displayed
  // label follows a language toggle (ennemis: French/English/Latin name), the
  // searched field must follow it too — otherwise searching "Alfalfa" in
  // English mode hits the French `client_display_name` and finds nothing.
  // Shape: { fr: [fieldPath, ...], en: [...], lat: [...] }. The matching
  // language's fields replace the `primary` entry in `searchFields`; the
  // secondary (synonym) entries that drive the grey "Synonyme : …" line are
  // kept as-is so synonyms stay searchable in every language.
  searchFieldsByLanguage = null,
  // Optional list of extra _source paths to include alongside each bucket.
  includeSourceFields = null,
  // Optional (option, sourceDict) => string — composes the displayed label
  // from backend extras.
  formatOptionLabel = null,
  // Optional info tooltip shown next to the label. { title, content }.
  infoBulle = null,
  // Tutorial slot key — renders a TutorialButton if assigned.
  tutorialSlotKey = null,
  // R7 (27 mai 26): static value→label mapping for filters whose ES buckets
  // are opaque integers/booleans (symbols health/env, approbation bio).
  // When set, the user's typed text never reaches the backend (it could
  // never match an integer bucket key) — instead, the cross-field aggregation
  // returns all present buckets, and client-side filter/rank operates on the
  // French label resolved from this map.
  valueLabels = null,
  // R7: optional explicit display order for static values (so symbol filters
  // show Aucun→Faible→...→Extrêmement élevé instead of alpha).
  valueOrder = null,
  // R78 (27 mai 26): label shown beside the globe icon. Defaults to the
  // singular "Langue"; PC ennemis overrides with "Langues" (plural)
  // because the dropdown carries multiple languages at once.
  langLabelKey = 'common|Langue',
  // R83-85 (27 mai 26): when set, render a second selector ("Type
  // d'affichage") next to the language selector. Switching the mode
  // swaps the ES field used as label_field on every option.
  // displayModeFields: { regroupement: 'display_name_regroupement', organisme: 'display_name_organisme' }
  displayModeFields = null,
  displayModeLabels = null,
  displayModeDescriptions = null,
  defaultDisplayMode = null,
  showDisplayModeSelector = false,
  displayModeToggleLabel = "Type d'affichage",
  // R82 + R85: { [mode]: { [lang]: esField } } cross-product used as the
  // label field when both a display-mode and language toggle are present.
  combinedLabelFields = null,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // -------------------------------------------------------------------------
  // UI state — the only state this component owns
  // -------------------------------------------------------------------------
  const [inputValue, setInputValue] = useState(''); // controlled desktop search input
  const [mobileSearchValue, setMobileSearchValue] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [localValue, setLocalValue] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (!languageFields) return null;
    const saved = localStorage.getItem(`${CONFIG.STORAGE_PREFIX}${field}`);
    return saved || defaultLanguage;
  });
  // R73 (27 mai 26): default to "regroupement" per spec. Persist in
  // localStorage like the language selector.
  const [selectedDisplayMode, setSelectedDisplayMode] = useState(() => {
    if (!displayModeFields) return null;
    const saved = localStorage.getItem(`${CONFIG.STORAGE_PREFIX}mode_${field}`);
    return saved || defaultDisplayMode || Object.keys(displayModeFields)[0];
  });
  // Lazy fetch: don't fire requests until the dropdown opens at least once,
  // unless the component already has selected values to resolve labels for.
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);

  const currentLabelField = useMemo(
    () => {
      // R82 + R85 (27 mai 26): when a filter has BOTH a display-mode toggle
      // and a language toggle (ennemis), the label field is the cross-product
      // — combinedLabelFields[mode][lang]. This makes a selected enemy's name
      // follow the language change (R82) AND the Type-d'affichage change (R85).
      if (combinedLabelFields && selectedDisplayMode && selectedLanguage) {
        const byMode = combinedLabelFields[selectedDisplayMode];
        const f = byMode && byMode[selectedLanguage];
        if (f) return f;
      }
      // Display-mode-only filter (no language dimension).
      if (displayModeFields && selectedDisplayMode) {
        const f = displayModeFields[selectedDisplayMode];
        if (f) return f;
      }
      return languageFields?.[selectedLanguage] || label_field;
    },
    [languageFields, selectedLanguage, label_field, displayModeFields, selectedDisplayMode, combinedLabelFields],
  );

  // Language-aware search fields: swap the `primary` search field(s) for the
  // ones matching the selected language so the searched field tracks the
  // displayed label (see searchFieldsByLanguage prop). Secondary entries (the
  // synonym fields carrying a sourceField, used for the grey "Synonyme" line)
  // are preserved unchanged.
  const effectiveSearchFields = useMemo(() => {
    if (!Array.isArray(searchFields)) return searchFields;
    const langPrimaries =
      searchFieldsByLanguage && selectedLanguage
        ? searchFieldsByLanguage[selectedLanguage]
        : null;
    if (!Array.isArray(langPrimaries) || langPrimaries.length === 0) {
      return searchFields;
    }
    const secondary = searchFields.filter(
      (sf) => typeof sf === 'object' && sf.sourceField && !sf.primary,
    );
    const primaries = langPrimaries.map((f) =>
      typeof f === 'string' ? { field: f, primary: true } : f,
    );
    return [...primaries, ...secondary];
  }, [searchFields, searchFieldsByLanguage, selectedLanguage]);

  // -------------------------------------------------------------------------
  // Debounced query — single source of truth fed to the data hook
  // -------------------------------------------------------------------------
  const activeRawInput = isMobile ? mobileSearchValue : inputValue;
  const debouncedQuery = useDebouncedValue(activeRawInput, debounceTimeout);

  const hasPreSelectedValues = useMemo(() => {
    if (Array.isArray(value)) return value.length > 0;
    return value != null && value !== '';
  }, [value]);

  // -------------------------------------------------------------------------
  // Data fetching — fully delegated to React Query
  // -------------------------------------------------------------------------
  // R7: when the filter is backed by a static value→label map (symbol
  // filters), the user's typed text must NOT reach the backend — the bucket
  // keys are integers and a `*fai*` wildcard could never match `2`. We feed
  // the backend an empty query so it returns the full set of buckets that
  // co-exist with sibling selections; the client filters/ranks those by the
  // French label below.
  const isStaticLabelFilter = !!valueLabels;
  const effectiveBackendQuery = isStaticLabelFilter ? '' : debouncedQuery;

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useElasticsearchOptions({
    document,
    field,
    query: effectiveBackendQuery,
    pageSize,
    sortBy,
    sortOrder,
    valueField: value_field,
    labelField: currentLabelField,
    filters,
    facets: activeFacets,
    searchFields: effectiveSearchFields,
    includeSourceFields,
    apiEndpoint,
    apiFilters,
    enabled: hasOpenedOnce || hasPreSelectedValues,
  });

  // Flatten infinite-query pages → raw options.
  const rawOptions = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((p) => p.options || []);
  }, [data]);

  // Persistent label cache so selected-tag labels don't disappear when the
  // options list refreshes (e.g. after a cross-facet refetch strips the
  // currently-selected value out of the visible bucket set).
  //
  // R50/R61/R82 (27 mai 26): the cache is keyed BY LANGUAGE / display-mode.
  // A selected culture's tag must follow the language toggle (concombre →
  // cucumber). A single flat cache leaked the previous language's label
  // (and could win when the selected value wasn't in the freshly-fetched
  // page), so the tag stayed in the old language. Per-language buckets mean
  // a value resolves only from the label cached for the CURRENT language.
  const labelCacheRef = useRef(new Map()); // Map<langKey, Map<value, label>>
  const cacheKey = `${selectedLanguage || ''}|${selectedDisplayMode || ''}`;
  const langLabelCache = () => {
    if (!labelCacheRef.current.has(cacheKey)) {
      labelCacheRef.current.set(cacheKey, new Map());
    }
    return labelCacheRef.current.get(cacheKey);
  };

  // Apply consumer formatting (formatOptionLabel, labelTransform) and
  // secondary-field match annotations. Pure derivation from rawOptions +
  // current query — no side effects, nothing to invalidate.
  const formattedOptions = useMemo(() => {
    const mapped = rawOptions.map((option) => {
      let resolvedLabel;
      // R7: static label map wins over any formatter — opaque bucket keys
      // like "2" become "Faible" before they touch any consumer code.
      if (valueLabels) {
        const mapped = valueLabels[String(option.value)] ?? valueLabels[option.value];
        if (mapped != null) resolvedLabel = mapped;
      }
      if (resolvedLabel == null && typeof formatOptionLabel === 'function') {
        resolvedLabel = formatOptionLabel(option, option.source || {});
      }
      if (resolvedLabel == null) {
        resolvedLabel = labelTransform
          ? labelTransform(option.label, option.value)
          : option.label;
      }
      return {
        label: resolvedLabel,
        value: option.value,
        data: {
          count: option.count,
          highlighted: option.highlighted,
          source: option.source,
          matchInfo: detectMatchInfo(option, debouncedQuery, effectiveSearchFields),
        },
      };
    });
    // R24/R25: "commence par" first, then "contient"; drop false positives.
    const ranked = rankAndFilterByQuery(mapped, debouncedQuery, effectiveSearchFields);
    // R7: when an explicit `valueOrder` was passed (e.g. symbol filters
    // Aucun→Faible→Léger→…), force that order unless the user is typing
    // (then ranking by query wins so "fai" still surfaces "Faible" first).
    if (Array.isArray(valueOrder) && !debouncedQuery) {
      const orderIdx = new Map(valueOrder.map((v, i) => [String(v), i]));
      ranked.sort((a, b) => {
        const ai = orderIdx.has(String(a.value)) ? orderIdx.get(String(a.value)) : 9999;
        const bi = orderIdx.has(String(b.value)) ? orderIdx.get(String(b.value)) : 9999;
        return ai - bi;
      });
    }
    return ranked;
  }, [rawOptions, formatOptionLabel, labelTransform, debouncedQuery, effectiveSearchFields, valueLabels, valueOrder]);

  // R82/R85: resolve labels for SELECTED values that the current page hasn't
  // loaded (large filters like Ennemis), in the current language/display-mode,
  // so their tags follow the toggle instead of sticking on the stale
  // selection-time label. Only the ids missing from the loaded options are
  // fetched, so small filters (Cultures) never make extra requests.
  const selectedValues = useMemo(
    () => (Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : [])),
    [value],
  );
  const unresolvedSelectedIds = useMemo(() => {
    if (selectedValues.length === 0) return [];
    const present = new Set(formattedOptions.map((o) => o.value));
    return selectedValues.filter((v) => !present.has(v));
  }, [selectedValues, formattedOptions]);
  const { data: resolvedSelectedLabels } = useSelectedOptionLabels({
    document,
    field,
    valueField: value_field,
    labelField: currentLabelField,
    ids: unresolvedSelectedIds,
    baseFilters: filters,
    enabled: !apiEndpoint,
  });

  // Build the resolver used by tags + mobile display. Priority (lowest →
  // highest): initialLabels (cold-start, may be a stale language) → the
  // CURRENT-language cache → freshly-fetched options → targeted current-
  // language resolution of selected ids. So a selected value's tag always
  // reflects the current language, whether or not it's in the loaded page.
  const labelMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(initialLabels)) {
      initialLabels.forEach((item) => {
        if (item?.value != null) {
          map.set(item.value, item.label || item.name || item.value);
        }
      });
    }
    const cache = langLabelCache();
    cache.forEach((label, val) => {
      map.set(val, label);
    });
    formattedOptions.forEach((opt) => {
      if (opt?.value != null) {
        map.set(opt.value, opt.label);
        cache.set(opt.value, opt.label);
      }
    });
    if (resolvedSelectedLabels) {
      Object.entries(resolvedSelectedLabels).forEach(([val, label]) => {
        map.set(val, label);
        cache.set(val, label);
      });
    }
    return map;
    // cacheKey is included so the map (and its cache bucket) recompute when
    // the language / display-mode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLabels, formattedOptions, cacheKey, resolvedSelectedLabels]);

  const getLabel = useCallback(
    (val) => labelMap.get(val) || val,
    [labelMap],
  );

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleSearch = useCallback((raw) => {
    setInputValue(raw);
  }, []);

  const handleLanguageChange = useCallback(
    (lang) => {
      setSelectedLanguage(lang);
      localStorage.setItem(`${CONFIG.STORAGE_PREFIX}${field}`, lang);
      // R50/R61/R82: no cache wipe needed — the label cache is keyed by
      // language (see labelCacheRef / cacheKey), so switching language
      // automatically resolves labels from that language's bucket. Changing
      // currentLabelField also changes the RQ query key → fresh fetch.
    },
    [field],
  );

  // R85: same shape as handleLanguageChange — toggling the display mode
  // changes which ES field is used as label_field, so RQ refetches and we
  // wipe the cache to refresh selected-tag labels.
  const handleDisplayModeChange = useCallback(
    (mode) => {
      setSelectedDisplayMode(mode);
      localStorage.setItem(`${CONFIG.STORAGE_PREFIX}mode_${field}`, mode);
      // No cache wipe — the cache is keyed by language|display-mode.
    },
    [field],
  );

  // R11: when reopening a dropdown that already has selections, scroll the
  // popup so the last-selected option is in view. AntD renders option DOM
  // nodes with class `.ant-select-item-option` and a `title` attribute on
  // the inner div; we look up by label-prefix via title because AntD does
  // not expose the value as a data attribute.
  const handleDropdownVisibleChange = useCallback((open) => {
    if (!open) {
      // Wipe the leftover query only when the dropdown CLOSES, so reopening
      // starts from the full list. While it's open we deliberately keep the
      // query across selections (see handleChange) — selecting an item must
      // not snap the list back to "all results".
      setInputValue('');
      return;
    }
    {
      setHasOpenedOnce(true);
      const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
      if (valueArray.length === 0) return;
      const lastValue = valueArray[valueArray.length - 1];
      // Defer past two frames so AntD finishes rendering options.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            const popups = document.querySelectorAll(
              '.elasticsearch-select-dropdown',
            );
            const popup = popups[popups.length - 1];
            if (!popup) return;
            const lastLabel = langLabelCache().get(lastValue);
            const items = popup.querySelectorAll(
              '.ant-select-item-option',
            );
            const targetText =
              typeof lastLabel === 'string'
                ? lastLabel.replace(/<[^>]+>/g, '').toLowerCase()
                : '';
            let target = null;
            items.forEach((node) => {
              if (target) return;
              if (
                targetText
                && node.textContent.toLowerCase().includes(targetText)
              ) {
                target = node;
              }
            });
            if (!target) {
              const selected = popup.querySelector(
                '.ant-select-item-option-selected',
              );
              target = selected;
            }
            if (target && typeof target.scrollIntoView === 'function') {
              target.scrollIntoView({ block: 'center' });
            }
          } catch (_e) { /* best-effort */ }
        });
      });
    }
  }, [value]);

  const handleScroll = useCallback(
    (event) => {
      if (!infinite || !hasNextPage || isFetchingNextPage) return;
      const { target } = event;
      const atBottom =
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 5;
      if (atBottom) fetchNextPage();
    },
    [infinite, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const handleRemoveTag = useCallback(
    (removedValue) => {
      const newValue = Array.isArray(value)
        ? value.filter((v) => v !== removedValue)
        : undefined;
      onChange?.(newValue);
    },
    [value, onChange],
  );

  const handleChange = useCallback(
    (newValue) => {
      const valueArray = Array.isArray(newValue)
        ? newValue
        : (newValue ? [newValue] : []);
      const selectedItems = valueArray.map((val) => ({
        value: val,
        label: getLabel(val),
      }));
      const isMultiple = mode === 'multiple';
      // Preserve the typed query in multiple mode so the user can keep picking
      // from the SAME filtered list (type "puceron", select one, and still see
      // the other "puceron" matches instead of the list snapping back to every
      // result). Single-select still clears since its dropdown closes on pick.
      // The leftover query is wiped when the dropdown closes
      // (handleDropdownVisibleChange).
      if (!isMultiple) {
        setInputValue('');
      }
      const returnValue = isMultiple ? valueArray : valueArray[0] || null;
      onChange(returnValue, selectedItems);
    },
    [getLabel, onChange, mode],
  );

  // External language sync (e.g. another instance of the same field changed
  // language and broadcast it via window event).
  useEffect(() => {
    if (!languageFields) return;
    const handler = (event) => handleLanguageChange(event.detail);
    const eventName = `languageChanged_${field}`;
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [field, languageFields, handleLanguageChange]);

  // R21 (27 mai 26): clear the typed search text when the page-level
  // "Réinitialiser" button fires. resetFilters broadcasts `filtersReset`;
  // we wipe both the desktop and mobile inputs so no orphaned text lingers
  // in the search bar after a reset.
  useEffect(() => {
    const handler = () => {
      setInputValue('');
      setMobileSearchValue('');
    };
    window.addEventListener('filtersReset', handler);
    return () => window.removeEventListener('filtersReset', handler);
  }, []);

  // Mobile: sync localValue + reset search when sheet opens.
  useEffect(() => {
    if (sheetOpen) {
      const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
      setLocalValue(valueArray.filter(Boolean));
      setMobileSearchValue('');
      setHasOpenedOnce(true);
    }
  }, [sheetOpen, value]);

  // Append loading sentinel for desktop infinite scroll.
  const selectOptions = useMemo(() => {
    // `title: ''` suppresses rc-select's native HTML `title` attribute on each
    // option (which otherwise pops the browser's plain tooltip on hover and
    // duplicates the styled AntD Tooltip rendered inside OptionRenderer).
    const out = formattedOptions.map((option) => ({ ...option, title: '' }));
    if (isFetchingNextPage && infinite) {
      out.push({
        value: '__loading__',
        label: TRANSLATIONS.LOADING,
        disabled: true,
        title: '',
      });
    }
    return out;
  }, [formattedOptions, isFetchingNextPage, infinite]);

  // Mobile handlers
  const handleMobileSearchChange = useCallback((e) => {
    setMobileSearchValue(e.target.value);
  }, []);

  const handleMobileSelect = useCallback(
    (optionValue) => {
      const isMultiple = mode === 'multiple';
      if (isMultiple) {
        setLocalValue((prev) =>
          prev.includes(optionValue)
            ? prev.filter((v) => v !== optionValue)
            : [...prev, optionValue],
        );
      } else {
        const selectedItem = { value: optionValue, label: getLabel(optionValue) };
        onChange([optionValue], [selectedItem]);
        setSheetOpen(false);
      }
    },
    [mode, getLabel, onChange],
  );

  const handleMobileDone = useCallback(() => {
    const selectedItems = localValue.map((val) => ({
      value: val,
      label: getLabel(val),
    }));
    onChange(localValue, selectedItems);
    setSheetOpen(false);
  }, [localValue, getLabel, onChange]);

  const handleMobileScroll = useCallback(
    (e) => {
      if (!infinite || !hasNextPage || isFetchingNextPage) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollTop + clientHeight >= scrollHeight - 50) fetchNextPage();
    },
    [infinite, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const mobileDisplayValue = useMemo(() => {
    const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
    if (valueArray.length === 0) return '';
    if (valueArray.length === 1) {
      return stripHtml(String(getLabel(valueArray[0])));
    }
    return t('common|{{count}} sélectionné(s)', { count: valueArray.length });
  }, [value, getLabel, t]);

  const isMultiple = mode === 'multiple';
  const selectedCount = localValue.length;

  // -------------------------------------------------------------------------
  // Mobile render
  // -------------------------------------------------------------------------
  if (isMobile) {
    return (
      <div className="w-full space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <label className="flex items-center gap-1 text-xs font-medium text-gray-700 min-w-0">
            <span className="truncate">{t(label)}</span>
            {infoBulle && (
              <Tooltip
                title={
                  <div>
                    {infoBulle.title && (
                      <div className="font-semibold mb-1">{t(infoBulle.title)}</div>
                    )}
                    <div>{t(infoBulle.content)}</div>
                  </div>
                }
                placement="top"
              >
                <Info
                  size={14}
                  className="text-gray-400 cursor-help"
                  style={{ flexShrink: 0 }}
                />
              </Tooltip>
            )}
            {tutorialSlotKey && (
              <TutorialButton slotKey={tutorialSlotKey} className="tutorial-btn--sm" />
            )}
          </label>
          {(showDisplayModeSelector || showLanguageSelector) && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {showDisplayModeSelector && displayModeFields && displayModeLabels && (
                <DisplayModeSelector
                  selectedMode={selectedDisplayMode}
                  modeLabels={displayModeLabels}
                  modeDescriptions={displayModeDescriptions}
                  onChange={handleDisplayModeChange}
                  toggleLabel={displayModeToggleLabel}
                />
              )}
              {showLanguageSelector && languageFields && languageLabels && (
                <LanguageSelector
                  selectedLanguage={selectedLanguage}
                  languageLabels={languageLabels}
                  onChange={handleLanguageChange}
                  langLabel={t(langLabelKey)}
                />
              )}
            </div>
          )}
        </div>

        <div
          onClick={() => setSheetOpen(true)}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span
            className={`text-xs flex-1 truncate ${
              mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            {mobileDisplayValue || placeholder || t('common|Search...')}
          </span>
          {isFetching ? (
            <Spin size="small" className="ml-2" />
          ) : (
            <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
          )}
        </div>

        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={
            isMultiple && selectedCount > 0
              ? `${t(label)} (${selectedCount})`
              : t(label)
          }
          showDone={isMultiple}
          onDone={handleMobileDone}
          doneLabel={t('common|Terminé')}
          // R34: allow Done with 0 selections so the user can deselect their
          // last remaining choice; otherwise the sheet traps them at >=1.
          doneDisabled={false}
        >
          {(showLanguageSelector || showDisplayModeSelector) && (
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 flex-wrap">
              {showDisplayModeSelector && displayModeFields && displayModeLabels && (
                <DisplayModeSelector
                  selectedMode={selectedDisplayMode}
                  modeLabels={displayModeLabels}
                  modeDescriptions={displayModeDescriptions}
                  onChange={handleDisplayModeChange}
                  toggleLabel={displayModeToggleLabel}
                />
              )}
              {showLanguageSelector && languageFields && languageLabels && (
                <LanguageSelector
                  selectedLanguage={selectedLanguage}
                  languageLabels={languageLabels}
                  onChange={handleLanguageChange}
                  langLabel={t(langLabelKey)}
                />
              )}
            </div>
          )}

          <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                placeholder={t('common|Rechercher...')}
                value={mobileSearchValue}
                onChange={handleMobileSearchChange}
              />
            </div>
          </div>

          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            onScroll={handleMobileScroll}
          >
            {isFetching && formattedOptions.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <Spin size="default" />
              </div>
            )}

            {!isFetching && formattedOptions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                <div className="text-xs">
                  {t('common|Aucun résultat trouvé')}
                </div>
              </div>
            )}

            {formattedOptions.length > 0 && (
              <div className="py-1">
                {formattedOptions.map((option) => {
                  const isSelected = localValue.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${
                        isSelected ? 'bg-green-50' : ''
                      }`}
                      onClick={() => handleMobileSelect(option.value)}
                    >
                      {isMultiple ? (
                        <div
                          className={`w-4 h-4 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                            isSelected
                              ? 'bg-[#207d54] border-[#207d54]'
                              : 'border-gray-300'
                          }`}
                        >
                          {isSelected && <Check size={10} color="#fff" />}
                        </div>
                      ) : (
                        <div
                          className={`w-4 h-4 border-2 rounded-full mr-3 flex-shrink-0 flex items-center justify-center ${
                            isSelected ? 'border-[#207d54]' : 'border-gray-300'
                          }`}
                        >
                          {isSelected && (
                            <div className="w-2 h-2 bg-[#207d54] rounded-full" />
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">
                          <HtmlContent html={option.label} />
                        </div>
                        {showCount && option.data?.count && (
                          <div className="text-xs text-gray-500">
                            {option.data.count} résultats
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {isFetchingNextPage && formattedOptions.length > 0 && (
              <div className="flex items-center justify-center py-4">
                <Spin size="small" />
              </div>
            )}
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Desktop render
  // -------------------------------------------------------------------------
  return (
    <div className="elasticsearch-select-container">
      <div className="select-header">
        {/* Top row: title + infobulle on the left (R86 — infobulle stays
            inline to the RIGHT of the title, never under it or splitting it),
            display-mode + language selectors on the right (R83 — sit beside
            the language/globe icon). */}
        <div className="select-header-row">
          <label className="select-label">
            <span className="select-label__text">{t(label)}</span>
            {infoBulle && (
              <Tooltip
                title={
                  <div>
                    {infoBulle.title && (
                      <div className="font-semibold mb-1">{t(infoBulle.title)}</div>
                    )}
                    <div>{t(infoBulle.content)}</div>
                  </div>
                }
                placement="top"
              >
                <Info
                  size={14}
                  className="text-gray-400 cursor-help"
                  style={{ flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </label>
          {(showDisplayModeSelector || showLanguageSelector) && (
            <div className="select-header-controls">
              {showDisplayModeSelector && displayModeFields && displayModeLabels && (
                <DisplayModeSelector
                  selectedMode={selectedDisplayMode}
                  modeLabels={displayModeLabels}
                  modeDescriptions={displayModeDescriptions}
                  onChange={handleDisplayModeChange}
                  toggleLabel={displayModeToggleLabel}
                />
              )}
              {showLanguageSelector && languageFields && languageLabels && (
                <LanguageSelector
                  selectedLanguage={selectedLanguage}
                  languageLabels={languageLabels}
                  onChange={handleLanguageChange}
                  langLabel={t(langLabelKey)}
                />
              )}
            </div>
          )}
        </div>
        {/* R87 (27 mai 26): the per-filter tutorial button sits UNDER the
            title (its own row) and uses a small variant — at full size,
            inline, it dominated and broke the header. */}
        {tutorialSlotKey && (
          <div className="select-header-tutorial">
            <TutorialButton slotKey={tutorialSlotKey} className="tutorial-btn--sm" />
          </div>
        )}
      </div>

      <Select
        ref={ref}
        key={`${field}-${selectedLanguage}`}
        className="elasticsearch-select"
        mode={mode}
        labelInValue={false}
        filterOption={false}
        onSearch={handleSearch}
        searchValue={inputValue}
        autoClearSearchValue={false}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        onPopupScroll={infinite ? handleScroll : undefined}
        placeholder={t('common|Search...') || TRANSLATIONS.SEARCH}
        notFoundContent={
          isFetching ? (
            <Spin size="small" />
          ) : (
            t('common|No results found') || TRANSLATIONS.NO_RESULTS
          )
        }
        loading={isFetching}
        value={value}
        onChange={handleChange}
        showSearch
        options={selectOptions}
        optionRender={(option) => (
          <OptionRenderer option={option} showCount={showCount} />
        )}
        // The collapsed "+N" indicator. rc-select passes the result of this as
        // the `label` to tagRender(isMaxTag). `omittedValues` is the ACTUAL set
        // of hidden tags, so its length is correct whatever number of tags
        // maxTagCount="responsive" decided to show (computing value.length - 1
        // assumed exactly one visible tag → "0 sélection" after the first pick).
        maxTagPlaceholder={(omittedValues) => {
          const n = Array.isArray(omittedValues) ? omittedValues.length : 0;
          return `+${n} ${n > 1 ? 'sélections' : 'sélection'}`;
        }}
        tagRender={(tagProps) => {
          const { value: tagValue, label: tagLabel, closable, onClose, isMaxTag } = tagProps;

          // Collapsed "+N sélection(s)" indicator — render the label rc-select
          // built from maxTagPlaceholder (correct hidden count).
          if (isMaxTag) {
            return <Tag style={{ marginRight: 3 }}>{tagLabel}</Tag>;
          }

          const displayLabel = String(getLabel(tagValue) || tagValue || '');
          const plainLabel = stripHtml(displayLabel);
          return (
            <Tooltip title={plainLabel}>
              <Tag
                closable={closable}
                onClose={onClose}
                className="es-multi-tag"
                style={{ marginRight: 3 }}
                // R40/R74: preserve italic markup (Bacillus, Achillea
                // millefolium). The Tag content is sanitized via
                // HtmlContent; truncation is handled by CSS ellipsis on the
                // flex text span so the × close icon always stays INSIDE the
                // tag — fixed pixel widths used to push it past the edge.
              >
                <span className="es-multi-tag__text">
                  <HtmlContent html={displayLabel} />
                </span>
              </Tag>
            </Tooltip>
          );
        }}
        suffixIcon={<img src={DownIcon} alt="Down" className="down-icon" />}
        popupClassName="elasticsearch-select-dropdown"
        {...props}
      />

      {showSelectedTags && (
        <CollapsibleTags
          value={value}
          getLabel={getLabel}
          onRemove={handleRemoveTag}
          maxVisible={maxVisibleTags}
        />
      )}
    </div>
  );
});

ElasticsearchSelect.displayName = 'ElasticsearchSelect';

export default React.memo(ElasticsearchSelect);